import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/Common/button';
import questionnaireService from '../../services/questionnaireService';

import { notify } from '../../lib/notify';

interface QuestionOption {
  id?: string;
  text: string;
  order: number;
  scale_level_identifier: string | null;
}

interface Question {
  id?: string;
  text: string;
  order: number;
  question_type: string;
  options: QuestionOption[];
  config: any;
  scoring_criterion_id: string | null;
}

interface Questionnaire {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  domain_id: string;
  questions: Question[];
}

const QuestionnaireEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracked separately from the (translated) error text so the metadata-only
  // fallback shows in every language.
  const [hasResponsesConflict, setHasResponsesConflict] = useState(false);

  useEffect(() => {
    const loadQuestionnaire = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await questionnaireService.get(id);
        setQuestionnaire(data);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || t('questionnaireEdit.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
  }, [id]);

  const handleSave = async () => {
    if (!questionnaire || !id) return;

    setSaving(true);
    setError(null);
    setHasResponsesConflict(false);

    try {
      // Prepare payload by removing IDs from questions and options for the update
      const payload = {
        name: questionnaire.name,
        description: questionnaire.description,
        questions: questionnaire.questions.map(q => ({
          text: q.text,
          order: q.order,
          question_type: q.question_type,
          config: q.config,
          scoring_criterion_id: q.scoring_criterion_id,
          options: q.options.map(opt => ({
            text: opt.text,
            order: opt.order,
            scale_level_identifier: opt.scale_level_identifier
          }))
        }))
      };

      await questionnaireService.update(id, payload);
      notify.success(t('questionnaireEdit.saveSuccess'));
      navigate(`/questionnaires/${id}`);
    } catch (e: any) {
      const errorMsg = e?.response?.data?.detail || e?.message || t('questionnaireEdit.saveError');
      if (errorMsg.includes('existing responses') || errorMsg.includes('réponses')) {
        setError(t('questionnaireEdit.responsesError'));
        setHasResponsesConflict(true);
      } else {
        setError(errorMsg);
      }
      notify.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMetadataOnly = async () => {
    if (!questionnaire || !id) return;

    setSaving(true);
    setError(null);
    setHasResponsesConflict(false);

    try {
      // Only send name and description, no questions
      const payload = {
        name: questionnaire.name,
        description: questionnaire.description
      };

      await questionnaireService.update(id, payload);
      notify.success(t('questionnaireEdit.metadataSuccess'));
      navigate(`/questionnaires/${id}`);
    } catch (e: any) {
      const errorMsg = e?.response?.data?.detail || e?.message || t('questionnaireEdit.saveError');
      setError(errorMsg);
      notify.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = () => {
    if (!questionnaire) return;

    const newOrder = questionnaire.questions.length > 0
      ? Math.max(...questionnaire.questions.map(q => q.order)) + 1
      : 1;

    setQuestionnaire({
      ...questionnaire,
      questions: [
        ...questionnaire.questions,
        {
          text: '',
          order: newOrder,
          question_type: 'text',
          options: [],
          config: null,
          scoring_criterion_id: null
        }
      ]
    });
  };

  const handleRemoveQuestion = (index: number) => {
    if (!questionnaire) return;

    setQuestionnaire({
      ...questionnaire,
      questions: questionnaire.questions.filter((_, i) => i !== index)
    });
  };

  const handleQuestionChange = (index: number, field: keyof Question, value: any) => {
    if (!questionnaire) return;

    const newQuestions = [...questionnaire.questions];
    newQuestions[index] = {
      ...newQuestions[index],
      [field]: value
    };

    setQuestionnaire({
      ...questionnaire,
      questions: newQuestions
    });
  };

  const handleAddOption = (questionIndex: number) => {
    if (!questionnaire) return;

    const question = questionnaire.questions[questionIndex];
    const newOrder = question.options.length > 0
      ? Math.max(...question.options.map(o => o.order)) + 1
      : 1;

    const newQuestions = [...questionnaire.questions];
    newQuestions[questionIndex] = {
      ...question,
      options: [
        ...question.options,
        {
          text: '',
          order: newOrder,
          scale_level_identifier: null
        }
      ]
    };

    setQuestionnaire({
      ...questionnaire,
      questions: newQuestions
    });
  };

  const handleRemoveOption = (questionIndex: number, optionIndex: number) => {
    if (!questionnaire) return;

    const newQuestions = [...questionnaire.questions];
    newQuestions[questionIndex] = {
      ...newQuestions[questionIndex],
      options: newQuestions[questionIndex].options.filter((_, i) => i !== optionIndex)
    };

    setQuestionnaire({
      ...questionnaire,
      questions: newQuestions
    });
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, field: keyof QuestionOption, value: any) => {
    if (!questionnaire) return;

    const newQuestions = [...questionnaire.questions];
    const newOptions = [...newQuestions[questionIndex].options];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      [field]: value
    };
    newQuestions[questionIndex] = {
      ...newQuestions[questionIndex],
      options: newOptions
    };

    setQuestionnaire({
      ...questionnaire,
      questions: newQuestions
    });
  };

  const questionTypeLabels: Record<string, string> = {
    'text': t('questionnaireDetail.text'),
    'single_choice': t('questionnaireDetail.single_choice'),
    'multiple_choice': t('questionnaireDetail.multiple_choice'),
    'scale': t('questionnaireDetail.scale'),
    'yes_no': t('questionnaireDetail.yes_no'),
    'slider': t('questionnaireDetail.slider')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('questionnaireEdit.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !questionnaire) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/questionnaires/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('questionnaireEdit.back')}
        </Button>
        <div className="bg-destructive/10 border-l-4 border-destructive text-destructive p-4 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/questionnaires/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('questionnaireEdit.back')}
          </Button>
          <h1 className="text-2xl font-bold text-foreground">{t('questionnaireEdit.title')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/questionnaires/${id}`)}>
            {t('questionnaireEdit.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('questionnaireEdit.saving') : t('questionnaireEdit.save')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border-l-4 border-destructive text-destructive p-4 rounded">
          <p className="font-semibold mb-2">{t('questionnaireEdit.error')}</p>
          <p>{error}</p>
          {(hasResponsesConflict || error.includes('réponses')) && (
            <div className="mt-3 pt-3 border-t border-destructive/20">
              <p className="text-sm mb-2">{t('questionnaireEdit.youCan')}</p>
              <ul className="text-sm list-disc list-inside space-y-1">
                <li>{t('questionnaireEdit.editMetadataOnly')}</li>
                <li>{t('questionnaireEdit.createNewFromThis')}</li>
              </ul>
              <Button
                onClick={handleSaveMetadataOnly}
                disabled={saving}
                className="mt-3"
                size="sm"
              >
                {t('questionnaireEdit.saveNameDescriptionOnly')}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{t('questionnaireEdit.generalInfo')}</h2>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('questionnaireEdit.questionnaireName')}
          </label>
          <input
            type="text"
            value={questionnaire.name}
            onChange={(e) => setQuestionnaire({ ...questionnaire, name: e.target.value })}
            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('organizationGeneralInfo.description')}
          </label>
          <textarea
            value={questionnaire.description || ''}
            onChange={(e) => setQuestionnaire({ ...questionnaire, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{t('questionnaireEdit.questionsSection')}</h2>
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground italic">
              {t('questionnaireEdit.responsesHint')}
            </div>
            <Button onClick={handleAddQuestion} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              {t('questionnaireEdit.addQuestion')}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {questionnaire.questions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('questionnaireEdit.noQuestionsHint')}
            </div>
          ) : (
            questionnaire.questions
              .sort((a, b) => a.order - b.order)
              .map((question, qIndex) => (
                <div key={qIndex} className="border border-border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                          {qIndex + 1}
                        </span>
                        <input
                          type="text"
                          value={question.text}
                          onChange={(e) => handleQuestionChange(qIndex, 'text', e.target.value)}
                          placeholder={t('questionnaireEdit.questionTextPlaceholder')}
                          className="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>

                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            {t('questionnaireEdit.questionType')}
                          </label>
                          <select
                            value={question.question_type}
                            onChange={(e) => handleQuestionChange(qIndex, 'question_type', e.target.value)}
                            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                          >
                            {Object.entries(questionTypeLabels).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            {t('questionnaireEdit.order')}
                          </label>
                          <input
                            type="number"
                            value={question.order}
                            onChange={(e) => handleQuestionChange(qIndex, 'order', parseInt(e.target.value) || 1)}
                            className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                            min="1"
                          />
                        </div>
                      </div>

                      {/* Options */}
                      {(question.question_type === 'single_choice' || question.question_type === 'multiple_choice') && (
                        <div className="mt-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-muted-foreground">{t('questionnaireDetail.options')}</label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddOption(qIndex)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              {t('questionnaireEdit.addOption')}
                            </Button>
                          </div>
                          {question.options.map((option, oIndex) => (
                            <div key={oIndex} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => handleOptionChange(qIndex, oIndex, 'text', e.target.value)}
                                placeholder={t('questionnaireEdit.optionPlaceholder', { index: oIndex + 1 })}
                                className="flex-1 px-3 py-1.5 border border-input bg-background text-foreground rounded text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveOption(qIndex, oIndex)}
                                aria-label={t('questionnaireEdit.removeOption')}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveQuestion(qIndex)}
                      className="ml-2"
                      aria-label={t('questionnaireEdit.removeQuestion')}
                    >
                      <Trash2 className="h-5 w-5 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireEditPage;

